<template>
  <div class="decrypt">
    <div class="bomb-timer"></div>
    <h2>Odszyfruj wiadomosć</h2>
    <p>
      Przed Tobą szyfrowane wiadomości. Twoim zadaniem jest je odszyfrować i
      wprowadzić poprawne teksty w pola poniżej.
    </p>

    <div v-for="(encrypted, index) in encryptedStrings" :key="index">
      <div class="encrypted-box">
        <p class="encrypted-text">{{ encrypted }}</p>
        <button
          @click="copyToClipboard(encrypted)"
          class="copy-button"
          title="Kopiuj do schowka"
        >
          kopiuj
        </button>
      </div>
      <input
        v-model="userInputs[index]"
        placeholder="Wprowadź odszyfrowany tekst"
      />
    </div>

    <div class="instructions">
      <button @click="showHints = !showHints" class="hint-btn">
        {{ showHints ? "Schowaj wskazówki" : "Pokaż wskazówki" }}
      </button>
      <div v-if="showHints" class="hints-list">
        <p>Wskazówki do odszyfrowania wiadomości:</p>
        <ol>
          <li>
            Pierwszy ciąg to kodowanie
            <a
              href="https://www.base64decode.org/"
              target="_blank"
              rel="noopener noreferrer"
              >Base64</a
            >.
          </li>
          <li>
            Drugi ciąg używa
            <a
              href="https://skalkuluj.pl/szyfr-cezara-online"
              target="_blank"
              rel="noopener noreferrer"
              >Szyfru Cezara</a
            >
            z przesunięciem o 1.
          </li>
          <li>
            Trzeci ciąg to
            <a
              href="https://www.rapidtables.com/convert/number/hex-to-ascii.html"
              target="_blank"
              rel="noopener noreferrer"
              >kod szesnastkowy</a
            >.
          </li>
        </ol>
      </div>
    </div>

    <button @click="checkAnswers">Zatwierdź</button>
  </div>

  <div v-if="decryptionCompleted" class="congrats-modal">
    <div class="modal-content">
      <h2>🎉 Gratulacje! 🎉</h2>
      <p>Udało Ci się odszyfrować wiadomości!</p>
      <button @click="claimReward" class="claim-btn">Powrót do menu</button>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";

const encryptedStrings = ref([
  "V2Vzb8WCeWNoIMWad2nEhXQh",
  "Tadaęśmjxfhp Opxfhp Splv!",
  "4E 6F 2C 20 49 27 6D 20 6E 6F 74 20 61 20 48 75 6D 61 6E",
]);

const correctAnswers = [
  "Wesołych Świąt!",
  "Szczęśliwego Nowego Roku!",
  "No, I'm not a Human",
];
const userInputs = ref(["", "", ""]);
const decryptionCompleted = ref(false);
const finalMessage = ref("");
const showHints = ref(false);

const checkAnswers = () => {
  if (
    userInputs.value.every((input, index) => input === correctAnswers[index])
  ) {
    decryptionCompleted.value = true;
    finalMessage.value = correctAnswers.join(" ");
    document.cookie = "puzzle2=completed; max-age=3600";
  }
};

const claimReward = () => {
  window.location.href = "/home";
};

const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text).catch((err) => {
    console.error("Failed to copy: ", err);
    alert("Failed to copy text.");
  });
};
</script>

<style scoped>
.decrypt {
  text-align: center;
  padding: 32px;
  background: #121212;
  color: #00ff00;
  box-shadow: 0 0 20px rgba(0, 255, 0, 0.3), inset 0 0 20px rgba(0, 255, 0, 0.2);
  font-family: "Courier New", monospace;
  position: relative;
  overflow: hidden;
  max-width: 600px;
  margin: 0 auto;
  animation: bombPulse 1.5s infinite alternate;
  min-height: 100vh;
}

.decrypt::before {
  content: "";
  position: absolute;
  top: -10px;
  left: -10px;
  right: -10px;
  bottom: -10px;
  background: repeating-linear-gradient(
    45deg,
    transparent,
    transparent 2px,
    rgba(0, 255, 0, 0.2) 2px,
    rgba(0, 255, 0, 0.2) 4px
  );
  z-index: -1;
  border-radius: 10px;
}

/* Animowana kreska */
.decrypt h2 {
  font-size: 1.8em;
  color: #ff5555;
  text-shadow: 0 0 5px #ff5555;
  margin-bottom: 24px;
  position: relative;
  display: inline-block;
  padding-bottom: 10px;
}

.decrypt h2::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(to right, transparent, #ff5555, transparent);
  animation: scanLine 1.5s linear infinite;
  transform: translateX(-100%);
}

/* Kontener dla zaszyfrowanego tekstu i guzika kopiowania */
.encrypted-box {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  background: #001100;
  padding: 12px;
  margin: 12px auto;
  border-left: 4px solid #ff5555;
  border-right: 4px solid #ff5555;
  width: 80%;
  font-family: monospace;
  white-space: pre-wrap;
  animation: textGlitch 1.5s infinite;
  word-break: break-all;
}

.encrypted-text {
  margin: 0;
  flex-grow: 1;
  text-align: left;
  word-break: break-all;
  overflow-wrap: break-word;
  padding-right: 8px;
}

.copy-button {
  background: transparent;
  border: none;
  color: #00ff00;
  cursor: pointer;
  padding: 0 8px;
  font-size: 0.9em;
  transition: color 0.3s;
  margin-left: 8px;
  white-space: nowrap;
}

.copy-button:hover {
  color: #ff5555;
}

input {
  background: #001100;
  color: #00ff00;
  border: 2px solid #009900;
  padding: 8px 12px;
  margin: 8px auto;
  width: 80%;
  font-family: monospace;
  caret-color: #00ff00;
  box-shadow: inset 0 0 10px rgba(0, 255, 0, 0.3);
  display: block;
}

input:focus {
  outline: none;
  box-shadow: 0 0 15px rgba(0, 255, 0, 0.5);
  border-color: #00ff00;
}

button {
  background: linear-gradient(to bottom, #ff5555, #cc0000);
  color: white;
  border: none;
  padding: 12px 24px;
  margin: 16px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-family: monospace;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 2px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s;
  box-shadow: 0 0 10px rgba(255, 0, 0, 0.5);
}

.hint-btn {
  background: linear-gradient(to bottom, #ffb555, #cc7400);
  box-shadow: 0 0 10px rgba(255, 166, 0, 0.5);
}

button:hover {
  background: linear-gradient(to bottom, #ff0000, #990000);
  box-shadow: 0 0 20px rgba(255, 0, 0, 0.8);
  transform: scale(1.05);
}

.hint-btn:hover {
  background: linear-gradient(to bottom, #ff9900, #be6d02);
  box-shadow: 0 0 10px rgba(255, 166, 0, 0.5);
}

a {
  color: #00ff00;
  text-decoration: underline;
}

button::after {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transition: 0.5s;
}

button:hover::after {
  left: 100%;
}

@media screen and (max-width: 600px) {
  .encrypted-box {
    flex-direction: column;
    align-items: flex-start;
  }
  .encrypted-text {
    width: 100%;
    text-align: left;
  }
  .copy-button {
    margin-top: 8px;
    align-self: flex-end;
  }
}

@keyframes bombPulse {
  0% {
    box-shadow: 0 0 20px rgba(0, 255, 0, 0.3);
  }
  100% {
    box-shadow: 0 0 30px rgba(255, 0, 0, 0.6);
  }
}

@keyframes scanLine {
  0% {
    transform: translateX(-150%);
  }
  100% {
    transform: translateX(150%);
  }
}

@keyframes wireFlicker {
  0% {
    opacity: 0.3;
  }
  100% {
    opacity: 1;
  }
}
</style>
